import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { sendPasswordResetLink } from '@/api/auth';
import { useFormErrors } from '@/hooks/useFormErrors';

/**
 * Request a password reset link — BUILD-PLAN §17, §29.
 *
 * The confirmation is deliberately non-committal about whether an account
 * exists, matching the backend response. Saying "we've emailed you" only
 * when the address is registered would let anyone test which addresses have
 * Glanz Luxe accounts.
 */
export function ForgotPasswordPage() {
  const { fieldErrors, formError, handleError, reset } = useFormErrors();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    reset();
    setSubmitting(true);

    try {
      await sendPasswordResetLink(email);
      setSent(true);
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your inbox"
        footer={
          <Link to="/login" className="text-accent underline underline-offset-4">
            Back to sign in
          </Link>
        }
      >
        <Alert tone="success">
          If an account exists for <strong>{email}</strong>, a link to reset your password is
          on its way. It expires in 60 minutes.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="Enter your email address and we’ll send you a link to choose a new password."
      footer={
        <Link to="/login" className="text-accent underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        {formError && <Alert tone="error">{formError}</Alert>}

        <Input
          label="Email address"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors['email']}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
        />

        <Button type="submit" loading={submitting} fullWidth size="lg">
          Send Reset Link
        </Button>
      </form>
    </AuthLayout>
  );
}
